import { Component, HostListener, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export type PartPhotoViewerData = {
  images: string[];
  index: number;
  title: string;
};

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.5;
const DRAG_CLICK_THRESHOLD_PX = 6;

/**
 * Полноэкранный просмотр фото запчасти (по образцу галереи b2c):
 * зум колесом/кликом, перетаскивание в приближении, стрелки и лента
 * миниатюр снизу, навигация с клавиатуры.
 */
@Component({
  selector: 'app-part-photo-viewer-dialog',
  templateUrl: './part-photo-viewer-dialog.component.html',
  styleUrls: ['./part-photo-viewer-dialog.component.scss']
})
export class PartPhotoViewerDialogComponent {
  readonly images: string[];

  readonly title: string;

  activeIndex = 0;

  zoom = MIN_ZOOM;

  offsetX = 0;

  offsetY = 0;

  private dragPointerId: number | null = null;

  private dragStartX = 0;

  private dragStartY = 0;

  private dragOriginX = 0;

  private dragOriginY = 0;

  private pointerMoved = false;

  constructor(
    private readonly dialogRef: MatDialogRef<PartPhotoViewerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: PartPhotoViewerData
  ) {
    this.images = (data.images || []).filter(Boolean);
    this.title = data.title || 'Фото запчасти';
    const index = Number(data.index) || 0;
    this.activeIndex = index >= 0 && index < this.images.length ? index : 0;
  }

  get activeImage(): string | null {
    return this.images[this.activeIndex] ?? null;
  }

  get hasMultipleImages(): boolean {
    return this.images.length > 1;
  }

  get isZoomed(): boolean {
    return this.zoom > MIN_ZOOM;
  }

  get canZoomIn(): boolean {
    return this.zoom < MAX_ZOOM;
  }

  get canZoomOut(): boolean {
    return this.zoom > MIN_ZOOM;
  }

  get canResetView(): boolean {
    return this.zoom !== MIN_ZOOM || this.offsetX !== 0 || this.offsetY !== 0;
  }

  get imageTransform(): string {
    return `translate3d(${this.offsetX}px, ${this.offsetY}px, 0) scale(${this.zoom})`;
  }

  close(): void {
    this.dialogRef.close();
  }

  selectImage(index: number): void {
    if (index < 0 || index >= this.images.length) {
      return;
    }
    this.activeIndex = index;
    this.resetView();
  }

  showPrevious(): void {
    if (!this.hasMultipleImages) return;
    this.activeIndex = (this.activeIndex - 1 + this.images.length) % this.images.length;
    this.resetView();
  }

  showNext(): void {
    if (!this.hasMultipleImages) return;
    this.activeIndex = (this.activeIndex + 1) % this.images.length;
    this.resetView();
  }

  changeZoom(delta: number): void {
    const nextZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Number((this.zoom + delta).toFixed(2))));
    this.zoom = nextZoom;
    if (nextZoom === MIN_ZOOM) {
      this.offsetX = 0;
      this.offsetY = 0;
    }
  }

  resetView(): void {
    this.zoom = MIN_ZOOM;
    this.offsetX = 0;
    this.offsetY = 0;
    this.dragPointerId = null;
  }

  onStageWheel(event: WheelEvent): void {
    event.preventDefault();
    this.changeZoom(event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP);
  }

  onStagePointerDown(event: PointerEvent): void {
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.pointerMoved = false;

    if (!this.isZoomed) return;
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
    this.dragPointerId = event.pointerId;
    this.dragOriginX = this.offsetX;
    this.dragOriginY = this.offsetY;
  }

  onStagePointerMove(event: PointerEvent): void {
    if (
      Math.abs(event.clientX - this.dragStartX) > DRAG_CLICK_THRESHOLD_PX ||
      Math.abs(event.clientY - this.dragStartY) > DRAG_CLICK_THRESHOLD_PX
    ) {
      this.pointerMoved = true;
    }

    if (this.dragPointerId !== event.pointerId) return;
    this.offsetX = this.dragOriginX + event.clientX - this.dragStartX;
    this.offsetY = this.dragOriginY + event.clientY - this.dragStartY;
  }

  onStagePointerEnd(event: PointerEvent): void {
    if (this.dragPointerId !== event.pointerId) return;
    this.dragPointerId = null;
  }

  onStageClick(): void {
    // Клик после перетаскивания — не зумим.
    if (this.pointerMoved) {
      this.pointerMoved = false;
      return;
    }
    this.changeZoom(ZOOM_STEP);
  }

  onImageError(index: number): void {
    const failedImage = this.images[index];
    if (!failedImage) return;
    this.images.splice(index, 1);
    if (!this.images.length) {
      this.close();
      return;
    }
    if (this.activeIndex >= this.images.length) {
      this.activeIndex = this.images.length - 1;
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowLeft':
        this.showPrevious();
        break;
      case 'ArrowRight':
        this.showNext();
        break;
      case '+':
      case '=':
        this.changeZoom(ZOOM_STEP);
        break;
      case '-':
      case '_':
        this.changeZoom(-ZOOM_STEP);
        break;
      case '0':
        this.resetView();
        break;
      default:
        break;
    }
  }
}
